import React, { useEffect, useRef } from 'react';

import { toolbarPanelStyle } from './toolbar-styles';
import { ToolbarButton } from './toolbar-button';

export type ToolbarPopoverProps = {
	title: string;
	/** Inner content of the trigger button */
	trigger: React.ReactNode;
	children: React.ReactNode;
	label?: string;
	disabled?: boolean;
	align?: 'left' | 'right';
	panelWidth?: number;
	/** Open state is owned by the toolbar so only one popover can be open at a time */
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
};

/**
 * A popover that lives INSIDE the toolbar's own DOM (never a portal), so the editors' `[data-sku-toolbar]`
 * blur guards keep the annotation's edit session -- and its selection -- alive while it is open.
 */
export const ToolbarPopover = (props: ToolbarPopoverProps): React.ReactElement => {
	const { title, trigger, children, label, disabled = false, align = 'left', panelWidth, isOpen, onOpenChange } = props;
	const rootRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleDocumentMouseDown = (e: MouseEvent): void => {
			const target = e.target as HTMLElement | null;
			if (target && rootRef.current?.contains(target)) return;
			onOpenChange(false);
		};
		if (isOpen) document.addEventListener('mousedown', handleDocumentMouseDown, true);
		return (): void => { document.removeEventListener('mousedown', handleDocumentMouseDown, true); };
	}, [isOpen, onOpenChange]);

	return (
		<div ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
			<ToolbarButton
				title={title}
				label={label}
				isActive={isOpen}
				disabled={disabled}
				onClick={() => onOpenChange(!isOpen)}
			>
				{trigger}
			</ToolbarButton>
			{isOpen ? (
				<div
					role="dialog"
					aria-label={title}
					style={{ ...toolbarPanelStyle, [align]: 0, width: panelWidth ? `${panelWidth}px` : undefined }}
				>
					{children}
				</div>
			) : null}
		</div>
	);
};
